import type { IAddressService } from '@/services/interfaces/IAddressService'
import type { Address } from '@/types/address'
import { env } from '@/lib/env'

export class GoogleAddressService implements IAddressService {
  private apiKey: string

  constructor() {
    this.apiKey = env.GOOGLE_MAPS_API_KEY
    if (!this.apiKey) {
      throw new Error('Google Maps API Key is required')
    }
  }

  async searchAddresses(query: string): Promise<Address[]> {
    if (!query || query.length < 2) {
      return []
    }

    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(
          query
        )}&language=pt-BR&components=country:br&key=${this.apiKey}`,
        { mode: 'cors' }
      )

      if (!response.ok) {
        throw new Error('Erro ao buscar endereços')
      }

      const data = await response.json()
      
      if (!data.predictions) {
        return []
      }

      // Get detailed information for each prediction
      const addresses = await Promise.all(
        data.predictions.map(async (prediction: any) => {
          try {
            return await this.getAddressDetails(prediction.place_id)
          } catch (error) {
            // If details fail, create a basic address
            return this.createBasicAddress(prediction)
          }
        })
      )

      return addresses.filter(Boolean)
    } catch (error) {
      console.error('Erro ao buscar endereços:', error)
      throw new Error('Erro ao buscar endereços')
    }
  }

  async getCurrentLocation(): Promise<Address> {
    console.log('🌐 GoogleAddressService.getCurrentLocation iniciado')
    
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        console.error('❌ Geolocalização não suportada')
        reject(new Error('Geolocalização não é suportada neste navegador'))
        return
      }

      console.log('📱 Chamando navigator.geolocation.getCurrentPosition...')
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords
            console.log('📍 Coordenadas obtidas:', { latitude, longitude })
            
            const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&language=pt-BR&key=${this.apiKey}`
            console.log('🌐 Fazendo chamada para Google Geocoding API...')
            
            const response = await fetch(url, { mode: 'cors' })
            console.log('📡 Resposta da API recebida:', response.status)

            if (!response.ok) {
              console.error('❌ Erro na resposta da API:', response.status, response.statusText)
              throw new Error('Erro ao obter localização')
            }

            const data = await response.json()
            console.log('📊 Dados da API:', data)
            
            if (!data.results || data.results.length === 0) {
              console.error('❌ Nenhum resultado encontrado')
              throw new Error('Nenhum endereço encontrado para sua localização')
            }

            const result = data.results[0]
            console.log('🏠 Primeiro resultado:', result)
            
            const address = this.parseGoogleResult(result)
            console.log('✅ Endereço parseado:', address)
            
            resolve(address)
          } catch (error) {
            reject(error)
          }
        },
        (error) => {
          console.error('Geolocation error:', error)
          let errorMessage = 'Erro ao acessar sua localização'
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Permissão de localização negada pelo usuário'
              break
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Localização indisponível'
              break
            case error.TIMEOUT:
              errorMessage = 'Timeout ao obter localização'
              break
            default:
              errorMessage = 'Erro desconhecido ao obter localização'
              break
          }
          
          reject(new Error(errorMessage))
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      )
    })
  }

  async getAddressDetails(addressId: string): Promise<Address> {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/place/details/json?place_id=${addressId}&fields=geometry,address_components,formatted_address&language=pt-BR&key=${this.apiKey}`,
        { mode: 'cors' }
      )

      if (!response.ok) {
        throw new Error('Erro ao obter detalhes do endereço')
      }

      const data = await response.json()
      
      if (!data.result) {
        throw new Error('Endereço não encontrado')
      }

      return this.parseGoogleResult(data.result, addressId)
    } catch (error) {
      console.error('Erro ao obter detalhes do endereço:', error)
      throw new Error('Erro ao obter detalhes do endereço')
    }
  }

  private parseGoogleResult(result: any, placeId?: string): Address {
    const components = result.address_components || []
    
    const getComponent = (types: string[]) => {
      const component = components.find((c: any) => 
        types.some(type => c.types.includes(type))
      )
      return component?.long_name || ''
    }

    const getShortComponent = (types: string[]) => {
      const component = components.find((c: any) => 
        types.some(type => c.types.includes(type))
      )
      return component?.short_name || ''
    }

    return {
      id: placeId || result.place_id || Date.now().toString(),
      formattedAddress: result.formatted_address || '',
      street: getComponent(['route']),
      number: getComponent(['street_number']),
      neighborhood: getComponent(['sublocality', 'sublocality_level_1']),
      city: getComponent(['locality', 'administrative_area_level_2']),
      state: getShortComponent(['administrative_area_level_1']),
      zipCode: getComponent(['postal_code']),
      country: getComponent(['country']),
      coordinates: {
        lat: result.geometry?.location?.lat || 0,
        lng: result.geometry?.location?.lng || 0
      }
    }
  }

  private createBasicAddress(prediction: any): Address {
    return {
      id: prediction.place_id,
      formattedAddress: prediction.description,
      street: prediction.structured_formatting?.main_text || '',
      neighborhood: '',
      city: prediction.structured_formatting?.secondary_text?.split(',')[0] || '',
      state: '',
      zipCode: '',
      country: 'Brasil',
      coordinates: { lat: 0, lng: 0 }
    }
  }
}